import DOMPurify from "dompurify";
import { compact, max } from "lodash";

const MODAL_Z_INDEX = 800;

// default options to use to purify rich text
export const PURIFY_OPTIONS = {
	ADD_ATTR: [
		"target", // "target" is needed for opening links in a new tab
	],
	ALLOW_ARIA_ATTR: false,
	ALLOW_DATA_ATTR: false,
	FORBID_ATTR: [
		"id",
		"name",
		"role",
		"tabindex",
		"wairole",
	],
	FORBID_TAGS: [
		"audio",
		"button",
		"embed",
		"iframe",
		"img",
		"input",
		"object",
		"script",
		"select",
		"source",
		"textarea",
		"video",
	],
};

// attributes not needed in print content
const PURIFY_PRINT_UNUSED_ATTRS = [
	"name",
	"role",
	"tabindex",
	"wairole",
];

// attributes needed in print content
// "href" is needed for hyperlinks
// "id" is needed for charting
// "widgetid" is needed for cleaning up views
const PURIFY_PRINT_USED_ATTRS = [
	"href",
	"id",
	"widgetid",
];


let _vScrollWidth = 0;
let _hScrollHeight = 0;

/** add CSS styles to the page */
export function injectCssStyles(css: string): void {
	const head = document.querySelector("head");
	const e = document.createElement("style");
	e.setAttribute("type", "text/css");
	e.appendChild(document.createTextNode(css));
	head.appendChild(e);
};

/** Convert file to a base64 string */
export async function convertFiletoBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader;
		reader.readAsDataURL(file);
		reader.onload = function (): void {
			return resolve(reader.result as string);
		};
		reader.onerror = function (reader): void {
			return reject(reader);
		};
	});
};

/** This function wraps innerHTML in a div so that we can treat it like an HTMLElement */
export function createElementWithCleanHTML(innerHTML: string): HTMLElement {
	const divElement = document.createElement("div");
	divElement.innerHTML = purifyForHtml(innerHTML);
	return divElement;
}

/** Add CSS class(es) contained within a single string to the given element */
export function elementAddCss(element: HTMLElement, css: string): void {
	css?.split(" ")
		.forEach((css) => {
			if (css) {
				element.classList.add(css);
			}
		});
}

/** smoothly scroll to the top of an element */
export function smoothScrollTop(element: Element): void {
	element.scrollTo({ top: 0, behavior: "smooth" });
}

/** Return just the trimmed inner text of a given HTMLElement or string */
export function getTrimmedInnerText(element: string | HTMLElement): string {
	let cleanElement;
	if (typeof element === "string") {
		cleanElement = createElementWithCleanHTML(element);
	} else {
		cleanElement = element;
	}
	return cleanElement.innerText.trim();
}

/** Return height of horizontal scroll bar */
export function getHorizontalScrollbarHeight(): number {
	if (!_hScrollHeight) {
		var div = document.createElement("div");
		div.style.overflow = "scroll";
		div.style.visibility = "hidden";
		div.style.position = "absolute";
		div.style.width = "100px";
		div.style.height = "100px";
		document.body.appendChild(div);
		_hScrollHeight = div.offsetHeight - div.clientHeight;
		document.body.removeChild(div);
	}

	return _hScrollHeight;
}

/** Return width of vertical scroll bar */
export function getVerticalScrollbarWidth(): number {
	if (!_vScrollWidth) {
		var div = document.createElement("div");
		div.style.overflow = "scroll";
		div.style.visibility = "hidden";
		div.style.position = "absolute";
		div.style.width = "100px";
		div.style.height = "100px";

		var div2 = document.createElement("div");
		div.appendChild(div2);

		document.body.appendChild(div);
		_vScrollWidth = preciseWidth(div) - preciseWidth(div2);
		document.body.removeChild(div);
	}

	return _vScrollWidth;
}

/** Return the exact height of the element */
export function preciseHeight(element: HTMLElement): number {
	return element.getBoundingClientRect().height;
}

/** Return the exact width of the element */
export function preciseWidth(element: HTMLElement): number {
	return element.getBoundingClientRect().width;
}

/**
 * Purify string for use in HTML string so that it does not contain any harmful content; this can be used
 * when value is coming from database
 * NOTE: If you use option ADD_ATTR to whitelist some attribute, then remember that Purify will still remove this allowed attribute if it's value is URI (URL)
 * to bypass this, use ADD_URI_SAFE_ATTR option (see FroalaEditorComponent.tsx for example)
 */
export function purifyForHtml(value: string, additionalOptions: Record<string, any> = {}): string {
	return DOMPurify.sanitize(value, { ...PURIFY_OPTIONS, ...additionalOptions });
}

/** Read JSON content from given file */
export async function readJSONFile<T = object>(file: File): Promise<T> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader;
		reader.readAsText(file);
		reader.onload = function (): void {
			const text = reader.result as string;
			const json = JSON.parse(text);
			return resolve(json);
		};
		reader.onerror = function (reader): void {
			return reject(reader);
		};
	});
};

/** register attribute that is needed for printing */
export function registerPrintAttribute(attr: string): void {
	PURIFY_PRINT_USED_ATTRS.push(attr);
}

/** remove attributes not necessary for print content from given element */
export function removeAttributesForPrint(element: HTMLElement): void {
	DOMPurify.sanitize(element, {
		ADD_TAGS: ["a"],
		ADD_ATTR: PURIFY_PRINT_USED_ATTRS,
		ALLOW_ARIA_ATTR: false,
		ALLOW_DATA_ATTR: false,
		FORBID_ATTR: PURIFY_PRINT_UNUSED_ATTRS,
		IN_PLACE: true,
	});
}

/** reset the width of scrollbars */
export function resetScrollbarSize(): void {
	_vScrollWidth = 0;
	_hScrollHeight = 0;
}

/** apply CSS variables values in given object where values are another variables*/
export function setCSSVariables(obj: Record<string, string>, element?: HTMLElement): void {
	element = element || document.querySelector(":root");
	const style = element.style;

	for (const [key, value] of Object.entries(obj)) {
		style.setProperty("--" + key, "var(--" + value + ")");
	}
}

/** apply CSS variables where values are constant */
export function setCSSConstants(obj: object, element?: HTMLElement): void {
	element = element || document.querySelector(":root");
	const style = element.style;

	for (const [key, value] of Object.entries(obj)) {
		style.setProperty("--" + key, value);
	}
}

/** find closest scrollable parent */
export function findClosestScrollableParent(element: HTMLElement): HTMLElement | null {
	while (element) {
		// Check if the element is scrollable
		const overflowY = window.getComputedStyle(element).overflowY;
		const isScrollable = overflowY === "auto" || overflowY === "scroll";

		if (isScrollable) {
			return element;
		}
		// Move up to the next parent element
		element = element.parentElement;
	}

	// No scrollable parent found
	return null;
}

/** get all dialogs sorted by z-index */
export function getAllDialogsSortedByZIndex(): Element[] {
	const dialogs = getAllDialogs();
	return dialogs.sort((a, b) => parseInt((getComputedStyle(a).zIndex || "0")) - parseInt((getComputedStyle(b).zIndex || "0")));
}

/** get all open dialogs in document */
export function getAllDialogs(includeUnderlay: boolean = false): Element[] {
	// there could be off screen element, for example, the Menu that is wrapped with MuiModal 
	const filteredDialogs = Array.from(document.getElementsByClassName("MuiModal-root")).filter((dialog) => !dialog.getAttribute("aria-hidden"));

	return [
		...(includeUnderlay ? document.getElementsByClassName("csi-view-dialog-wrapper") : []),
		...document.getElementsByClassName("dijitDialog"),
		...filteredDialogs,
	];
}

/** return true if element is a react dialog */
export function isReactDialog(element: Element): boolean {
	return element.classList.contains("MuiModal-root");
}

/** return true if dialogs with greatest z-index is a react dialog */
export function isReactDialogWithHighestZIndex(): boolean {
	const dialogs = getAllDialogsSortedByZIndex();
	return isReactDialog(dialogs[dialogs.length - 1]);
}

/** return open react dialogs */
export function getReactDialogs(): Set<Element> {
	return new Set(Array.from(document.getElementsByClassName("MuiModal-root")));
}

/** take a string and return that same string with double quotes and backslashes escaped */
export function escapeDataAttributeSelectorValue(value: string, allowBackslash?: boolean): string {
	return allowBackslash ? value.replace(/"/g, "\\\"") : value.replace(/\\/g, "\\\\")
		.replace(/"/g, "\\\"");
}

/** Return browser information */
export function getBrowserInfo(): { name: string; version: string } {
	const userAgent = navigator.userAgent.toLowerCase();
	let name = "unknown";
	let version = "0";

	const browsers = ["edge", "opera", "chrome", "safari", "firefox", "msie", "trident"];
	for (const browser of browsers) {
		const match = userAgent.match(new RegExp(`(${browser})/?\\s*(\\d+)`, "i"));
		if (match) {
			name = match[1];
			version = match[2];
			break; // Stop checking once a match is found
		}
	}

	if (name === "trident" || name === "msie") {
		throw new Error("Unsupported browser");
	}

	return { name, version };
}

/** Return true if browser is FF */
export function isFirefox(): boolean {
	return getBrowserInfo().name === "firefox";
}

/** use a z-index that goes above the current dialogs in the document (if any) */
export function getDialogZIndex(): number {
	// get array of open dialogs
	const dialogArray = getAllDialogs(true);
	// get z-index of each, running compact to eliminate possible NaN values from parseInt
	const zArray = compact(dialogArray.map((x) => parseInt(getComputedStyle(x).zIndex)));
	// get the max z-index of the set of z-index values, include a default in case none found
	const zMax = max([...new Set([MODAL_Z_INDEX, ...zArray])]);
	// return either the new max plus two or the default base z-index for dialogs
	return zMax === MODAL_Z_INDEX ? MODAL_Z_INDEX : zMax + 2;
}